import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Activity } from '@personical/cc-shared-types';
import { useApi } from '../components/api-context.js';

const ACTION_LABEL: Record<string, string> = {
  'contact.soft_deleted': 'Moved to trash',
  'contact.restored': 'Restored from trash',
  'contact.hard_deleted': 'Permanently deleted',
  'settings.defaultCountry_changed': 'Default country changed'
};

export function AuditLogPage() {
  const api = useApi();
  const [items, setItems] = useState<Activity[] | null>(null);
  const [continuation, setContinuation] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    api
      .listAuditLog()
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setContinuation(res.continuationToken);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  async function loadMore() {
    if (!continuation || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api.listAuditLog({ continuationToken: continuation });
      setItems((prev) => [...(prev ?? []), ...res.items]);
      setContinuation(res.continuationToken);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold">Audit log</h2>
      <p className="text-sm text-mid">
        Tenant-wide actions: deletes, restores, settings changes. Per-contact call /
        note history lives on the contact's own detail page.
      </p>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-900 text-red-800 dark:text-red-300 text-sm rounded p-3">{error}</div>
      )}

      {items === null ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : items.length === 0 ? (
        <div className="surface rounded-2xl shadow p-8 text-center">
          <p className="text-sm text-mid">Nothing here yet.</p>
        </div>
      ) : (
        <ul className="surface rounded-2xl shadow divide-y divide-divider">
          {items.map((a) => (
            <li key={a.id} className="px-5 py-3 text-sm">
              <p className="font-medium">
                {a.action ? ACTION_LABEL[a.action] ?? a.action : 'audit'}
              </p>
              <p className="text-xs text-muted mt-0.5">
                {new Date(a.timestamp).toLocaleString()}
                {a.notes && <> · {a.notes}</>}
                {a.contactId && (
                  <>
                    {' '}·{' '}
                    <Link
                      to={`/contacts/${a.contactId}`}
                      className="underline text-mid hover:text-strong"
                    >
                      view contact
                    </Link>
                  </>
                )}
              </p>
            </li>
          ))}
        </ul>
      )}

      {continuation && (
        <div className="flex justify-center pt-2">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="btn-secondary px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}
